import React, { useState } from 'react';
import { apiFetch } from './lib/api';

const formatCurrency = (amount: number) => {
	if (amount === undefined || amount === null) return 'SAR 0';
	return new Intl.NumberFormat('en-SA', {
		style: 'currency',
		currency: 'SAR',
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(amount);
};

const formatPercentage = (value: number) => {
	if (value === undefined || value === null) return '0%';
	return `${(value * 100).toFixed(1)}%`;
};

const getSubsidyType = (redfCalc: any): { label: string; color: string } => {
	if (redfCalc?.fundValue && redfCalc.fundValue > 0) {
		return { label: 'EMI', color: 'bg-blue-100 text-blue-800' };
	}
	if (redfCalc?.downPaymentSupport && redfCalc.downPaymentSupport > 0) {
		return { label: 'Down Payment Support', color: 'bg-purple-100 text-purple-800' };
	}
	return { label: 'None', color: 'bg-gray-100 text-gray-600' };
};

interface RedfIntegrationSectionProps {
	baseUrl: string;
	applicationId: string;
}

const RedfIntegrationSection: React.FC<RedfIntegrationSectionProps> = ({ baseUrl, applicationId }) => {
	const [data, setData] = useState<any>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');
	const [hasFetched, setHasFetched] = useState(false);

	const fetchRedfData = async () => {
		setIsLoading(true);
		setError('');
		try {
			const response = await apiFetch(`${baseUrl}/api/v1/application/redf-integration-data/${applicationId}`);
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			const result = await response.json();
			if (result.success && result.data) {
				setData(result.data);
			} else {
				throw new Error(result.message || 'Failed to fetch REDF integration data');
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'An error occurred');
		} finally {
			setIsLoading(false);
			setHasFetched(true);
		}
	};

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<div className="flex items-center justify-between mb-4 border-b pb-2">
				<h3 className="text-lg font-semibold text-gray-800">REDF Integration Data</h3>
				<button
					onClick={fetchRedfData}
					disabled={isLoading}
					className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors font-medium">
					{isLoading ? 'Fetching...' : hasFetched ? 'Refresh' : 'Fetch REDF Data'}
				</button>
			</div>

			{error && (
				<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
					<strong>Error:</strong> {error}
				</div>
			)}

			{isLoading && (
				<div className="flex items-center justify-center py-8">
					<svg
						className="animate-spin h-6 w-6 text-green-600 mr-2"
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24">
						<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
						<path
							className="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
						/>
					</svg>
					<span className="text-gray-500 text-sm">Loading REDF integration data...</span>
				</div>
			)}

			{!isLoading && data && (
				<div className="space-y-6">
					{/* Summary */}
					{data.summary && (
						<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
							<div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
								<div className="text-xs text-gray-500 mb-1">Total Fund Value</div>
								<div className="text-xl font-bold text-green-700">{formatCurrency(data.summary.totalFundValue)}</div>
							</div>
							<div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
								<div className="text-xs text-gray-500 mb-1">Total Guarantee Value</div>
								<div className="text-xl font-bold text-blue-700">{formatCurrency(data.summary.totalGuaranteeValue)}</div>
							</div>
							<div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center">
								<div className="text-xs text-gray-500 mb-1">Applicant Count</div>
								<div className="text-xl font-bold text-gray-700">{data.summary.applicantCount}</div>
							</div>
						</div>
					)}

					{/* Per-Applicant REDF Data */}
					{data.redfData && Object.entries(data.redfData).map(([nationalId, entry]: [string, any]) => {
						const redfCalc = entry.redfCalculation;
						const subsidyType = getSubsidyType(redfCalc);

						return (
							<div key={nationalId} className="border rounded-lg p-4 bg-gray-50">
								{/* Header with National ID and eligibility badge */}
								<div className="flex items-center justify-between mb-4">
									<div className="flex items-center gap-3">
										<h4 className="font-semibold text-gray-800 text-sm">National ID: {nationalId}</h4>
										<span className={`px-2 py-1 rounded text-xs font-bold ${subsidyType.color}`}>
											Subsidy: {subsidyType.label}
										</span>
									</div>
									<span
										className={`px-2 py-1 rounded text-xs font-bold ${
											entry.isEligible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
										}`}>
										{entry.isEligible ? 'ELIGIBLE' : 'NOT ELIGIBLE'}
									</span>
								</div>

								{/* Key Metrics Grid */}
								<div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
									<div className="bg-white p-3 rounded border text-center">
										<div className="text-xs text-gray-500 mb-1">Fund Value</div>
										<div className="text-lg font-bold text-green-600">{formatCurrency(entry.fundValue)}</div>
									</div>
									<div className="bg-white p-3 rounded border text-center">
										<div className="text-xs text-gray-500 mb-1">Guarantee Value</div>
										<div className="text-lg font-bold text-blue-600">{formatCurrency(entry.guaranteeValue)}</div>
									</div>
									{redfCalc && (
										<>
											<div className="bg-white p-3 rounded border text-center">
												<div className="text-xs text-gray-500 mb-1">Guarantee Percentage</div>
												<div className="text-lg font-bold text-indigo-600">{formatPercentage(redfCalc.guaranteePercentage)}</div>
											</div>
											<div className="bg-white p-3 rounded border text-center">
												<div className="text-xs text-gray-500 mb-1">Down Payment Support</div>
												<div className="text-lg font-bold text-purple-600">{formatCurrency(redfCalc.downPaymentSupport)}</div>
											</div>
										</>
									)}
								</div>

								{/* REDF Calculation Details (collapsible) */}
								{redfCalc && (
									<details className="bg-white rounded border p-3 text-xs">
										<summary className="cursor-pointer font-medium text-gray-700">REDF Calculation Details</summary>
										<div className="mt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
											<div className="p-2 bg-gray-50 rounded">
												<span className="text-gray-500 block text-[10px] uppercase">Salary</span>
												<span className="font-semibold">{formatCurrency(redfCalc.salary)}</span>
											</div>
											<div className="p-2 bg-gray-50 rounded">
												<span className="text-gray-500 block text-[10px] uppercase">Property Price</span>
												<span className="font-semibold">{formatCurrency(redfCalc.propertyPrice)}</span>
											</div>
											<div className="p-2 bg-gray-50 rounded">
												<span className="text-gray-500 block text-[10px] uppercase">Down Payment</span>
												<span className="font-semibold">{formatCurrency(redfCalc.downPayment)}</span>
											</div>
											<div className="p-2 bg-gray-50 rounded">
												<span className="text-gray-500 block text-[10px] uppercase">Period</span>
												<span className="font-semibold">{redfCalc.periodInMonths} months</span>
											</div>
											<div className="p-2 bg-gray-50 rounded">
												<span className="text-gray-500 block text-[10px] uppercase">Profit Rate</span>
												<span className="font-semibold">{formatPercentage(redfCalc.profitRate)}</span>
											</div>
											<div className="p-2 bg-gray-50 rounded">
												<span className="text-gray-500 block text-[10px] uppercase">Total Repay Amount</span>
												<span className="font-semibold">{formatCurrency(redfCalc.totalRepayAmount)}</span>
											</div>
											<div className="p-2 bg-gray-50 rounded">
												<span className="text-gray-500 block text-[10px] uppercase">Building Type</span>
												<span className="font-semibold">{redfCalc.buildingType === 2 ? 'Apartment' : redfCalc.buildingType === 1 ? 'Villa' : redfCalc.buildingType}</span>
											</div>
											<div className="p-2 bg-gray-50 rounded">
												<span className="text-gray-500 block text-[10px] uppercase">Family Members</span>
												<span className="font-semibold">{redfCalc.familyMembersCount}</span>
											</div>
											<div className="p-2 bg-gray-50 rounded">
												<span className="text-gray-500 block text-[10px] uppercase">Global DBR</span>
												<span className="font-semibold">{redfCalc.globalDbr}%</span>
											</div>
											<div className="p-2 bg-gray-50 rounded">
												<span className="text-gray-500 block text-[10px] uppercase">Moyasser Value</span>
												<span className="font-semibold">{formatCurrency(redfCalc.moyasserValue)}</span>
											</div>
											<div className="p-2 bg-gray-50 rounded">
												<span className="text-gray-500 block text-[10px] uppercase">Response Status</span>
												<span className={`font-semibold ${redfCalc.responseCode?.result === 'SUCCESSFUL' ? 'text-green-600' : 'text-red-600'}`}>
													{redfCalc.responseCode?.result || 'N/A'}
												</span>
											</div>
											<div className="p-2 bg-gray-50 rounded">
												<span className="text-gray-500 block text-[10px] uppercase">Financing Guarantee</span>
												<span className="font-semibold">{formatCurrency(redfCalc.valueOfFinancingGuarantee)}</span>
											</div>
										</div>
									</details>
								)}
							</div>
						);
					})}
				</div>
			)}

			{!isLoading && !data && hasFetched && !error && (
				<div className="text-center py-6 text-gray-400 text-sm">No REDF integration data available.</div>
			)}

			{!hasFetched && !isLoading && (
				<div className="text-center py-6 text-gray-400 text-sm">
					Click "Fetch REDF Data" to load REDF integration data for this application.
				</div>
			)}
		</div>
	);
};

export default RedfIntegrationSection;
